import { ArnClient, ArnClientError, ArnLogger } from '@arianee/arn-client';

/**
 * A browser global scope that contains an ARN Client reference.
 */
export type ArnWindow = {
  arnClient: ArnClient;
};

/**
 * Provides an ARN Client instance to be called from the components.
 *
 * This allows to get such instance from other means than the window global scope.
 * For instance, Vue.js users might prefer to get it from Vue.js' global properties.
 */
export interface ArnClientProvider {
  get(): ArnClient;
}

/**
 * Provide the ARN Client reference from the window/globalThis scope.
 */
export class WindowArnClientProvider implements ArnClientProvider {
  get(): ArnClient {
    const arnClient = (window as unknown as ArnWindow).arnClient;
    if (!arnClient) {
      throw new ArnClientError('Could not find arnClient in global scope');
    }
    return arnClient;
  }
}

/**
 * The ARN Client provider used at web components initialization.
 */
export let defaultArnClientProvider: ArnClientProvider;

/**
 * Change the default ARN Client provider.
 *
 * @param provider
 */
export function setArnClientProvider(provider: ArnClientProvider) {
  defaultArnClientProvider = provider;
}

// Set the default ARN Client provider to the one looking from the global scope.
setArnClientProvider(new WindowArnClientProvider());

/**
 * An ARN Component relying on an ARN Client.
 */
export abstract class ArnComponent extends HTMLElement {
  /**
   *The ARN Client instance to rely on.
   */
  protected readonly arnClient: ArnClient;

  protected readonly log: ArnLogger;

  /**
   *
   * @param arnClientProvider The ARN Client instance to rely on. Will use one found in global scope/window by default.
   */
  constructor(arnClientProvider = defaultArnClientProvider) {
    super();
    this.arnClient = arnClientProvider.get();
    this.log = this.arnClient.log.fork(this.constructor.name); // this.template.host.localName
  }

  protected createSlot(name: string) {
    const slotElem = document.createElement('slot') as HTMLSlotElement;
    slotElem.name = name;
    return slotElem;
  }
}
